'use client';

import type { SyntheticEvent } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './style.css';
import type { NativeVideoPlayerError, NativeVideoPlayerHandle, VideoPlayerRef } from '@/app/types';
import { cn } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import type { BaseVideoPlayerProps } from './types';

const toNativeVideoError = (video: HTMLVideoElement | null, src: string, error: MediaError | Event | null) => ({
  playerVersion: 'native' as const,
  src,
  currentTime: video?.currentTime ?? 0,
  duration: video?.duration ?? 0,
  ended: video?.ended ?? false,
  networkState: video?.networkState ?? 0,
  readyState: video?.readyState ?? 0,
  errorCode: error instanceof MediaError ? error.code : video?.error?.code,
  errorType: 'native_video_error' as const,
  errorDetails: error instanceof MediaError ? error.message : video?.error?.message || 'native video error',
  message: error instanceof MediaError ? error.message : video?.error?.message || 'native video error',
  error,
});

const createPlayerHandle = (video: HTMLVideoElement, initialLang: string): NativeVideoPlayerHandle => ({
  get currentTime() {
    return video.currentTime;
  },
  set currentTime(value: number) {
    video.currentTime = value;
  },
  get paused() {
    return video.paused;
  },
  get lang() {
    return video.lang;
  },
  set lang(value: string) {
    video.lang = value || initialLang;
  },
  play: () => video.play(),
  pause: () => video.pause(),
  destroy: () => {
    video.pause();
    video.removeAttribute('src');
    video.load();
  },
  replay: async () => {
    video.currentTime = 0;
    await video.play();
  },
  on: (event, handler) => video.addEventListener(event, handler),
  off: (event, handler) => video.removeEventListener(event, handler),
});

/*
 * 基础原生视频播放器组件，负责核心播放器功能。
 */
export const BaseVideoPlayer = forwardRef<VideoPlayerRef, BaseVideoPlayerProps>(
  (
    {
      className,
      src,
      poster,
      startTime,
      autoPlay,
      isLazy = false,
      hasAudio = false,
      defaultVolume = 0,
      /**
       * 是否在 hover 时自动播放，
       * 为true时，hover 时自动播放，离开时自动暂停。
       * 为false时，hover 时自动播放，离开时自动暂停。
       */
      playOnHover = false,
      /**
       * 是否隐藏 controls，
       * 为true时，全程隐藏 controls，
       * 为false时， controls 的展示情况由 initShowControls 和 hoverTogglesControls 决定
       */
      hideControls = false,
      /**
       * 在播放之前是否显示 controls，
       * 在 hideControls 为false时，为true时，在播放之前显示 controls
       *                          为false时，在播放之前不显示 controls
       */
      initShowControls = false,
      /**
       * 是否在 hover 时影响 controls 的开关，
       * 在 hideControls 为false时，hoverTogglesControls 为true时，hover 时 controls 会显示，离开时 controls 会隐藏。
       *                           hoverTogglesControls 为false时，hover 时 controls 固定展示，不随hover变化。
       */
      hoverTogglesControls = false,
      enableSpaceShortcut = false,
      showStartPlugin = false,
      onPlayEnd,
      handleClickVideo,
      onReady,
      onError,
    },
    ref,
  ) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const playerRef = useRef<NativeVideoPlayerHandle | null>(null);
    const nativeLangRef = useRef('en');
    const onPlayEndRef = useRef(onPlayEnd);
    const onReadyRef = useRef(onReady);
    const onErrorRef = useRef(onError);
    onPlayEndRef.current = onPlayEnd;
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
    const locale = useLocale();
    const nativeLang = locale === 'zh' ? 'zh-cn' : 'en';
    nativeLangRef.current = nativeLang;
    const [isHovering, setIsHovering] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const showControls = !hideControls && (initShowControls || !hoverTogglesControls || isHovering);

    const getPlayerHandle = () => {
      const video = videoRef.current;
      if (!video) return null;
      if (!playerRef.current) {
        playerRef.current = createPlayerHandle(video, nativeLangRef.current);
      }
      return playerRef.current;
    };

    useEffect(() => {
      const player = getPlayerHandle();
      if (player) {
        player.lang = nativeLang;
      }
    }, [nativeLang]);

    useImperativeHandle(
      ref,
      () => ({
        get player() {
          return getPlayerHandle();
        },
        play: async () => {
          try {
            await videoRef.current?.play();
          } catch (error) {
            console.warn('Video play failed:', error);
          }
        },
        pause: () => {
          videoRef.current?.pause();
        },
        destroy: () => {
          playerRef.current?.destroy();
          playerRef.current = null;
        },
        getVideoElement: () => videoRef.current,
      }),
      [],
    );

    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;
      video.volume = hasAudio ? defaultVolume : 0;
      video.muted = !hasAudio;
    }, [defaultVolume, hasAudio]);

    useEffect(() => {
      const video = videoRef.current;
      if (!video || startTime === undefined) return;
      video.currentTime = startTime;
    }, [startTime, src]);

    useEffect(() => {
      const video = videoRef.current;
      if (!video || !autoPlay) return;
      video.play().catch((error) => {
        console.warn('Video autoplay failed:', error);
      });
    }, [autoPlay, src]);

    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const syncPlaying = () => {
        setIsPlaying(!video.paused && !video.ended);
      };

      syncPlaying();
      video.addEventListener('play', syncPlaying);
      video.addEventListener('pause', syncPlaying);
      video.addEventListener('ended', syncPlaying);
      return () => {
        video.removeEventListener('play', syncPlaying);
        video.removeEventListener('pause', syncPlaying);
        video.removeEventListener('ended', syncPlaying);
      };
    }, [src]);

    useEffect(() => {
      const video = videoRef.current;
      if (!video || !enableSpaceShortcut) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.code !== 'Space' || document.activeElement !== video) return;
        event.preventDefault();
        if (video.paused) {
          video.play().catch((error) => {
            console.warn('Video keyboard play failed:', error);
          });
        } else {
          video.pause();
        }
      };

      video.addEventListener('keydown', handleKeyDown);
      return () => video.removeEventListener('keydown', handleKeyDown);
    }, [enableSpaceShortcut]);

    const handleMouseEnter = () => {
      setIsHovering(true);
      if (!playOnHover) return;
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = 0;
      video.play().catch((error) => {
        console.warn('Auto-play on hover failed:', error);
      });
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
      if (playOnHover) {
        videoRef.current?.pause();
      }
    };

    const handleEnded = () => {
      const onEnd = onPlayEndRef.current;
      if (!onEnd) return;
      onEnd();
      const video = videoRef.current;
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => undefined);
      }
    };

    const handleError = (event: SyntheticEvent<HTMLVideoElement>) => {
      onErrorRef.current?.({
        error: toNativeVideoError(
          event.currentTarget,
          src,
          event.currentTarget.error || event.nativeEvent,
        ) as NativeVideoPlayerError,
        src,
      });
    };

    const handleStartClick = () => {
      videoRef.current?.play().catch((error) => {
        console.warn('Video start play failed:', error);
      });
    };

    return (
      <div
        className={cn(
          'native-video-player-container relative h-full w-full [--video-radius:8px] md:[--video-radius:0]',
          className,
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <video
          ref={videoRef}
          className="h-full w-full object-contain"
          src={src}
          poster={poster}
          autoPlay={autoPlay}
          muted={!hasAudio}
          controls={showControls}
          // 禁掉国产内核在视频上强制叠的原生控件：画中画、投屏、下载。
          // Chromium 系（含 MIUI Browser）认这三个标准属性；倍速等国产内核独有的
          // 浮层按钮属于 WebView 之外的原生 UI，标准 API 无法关闭。
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload noremoteplayback noplaybackrate"
          loop={playOnHover}
          playsInline
          // 国产 Android 内核在 HTML parse 阶段决定 inline / 原生浮层播放，需首屏写入。
          webkit-playsinline="true"
          x5-playsinline="true"
          x5-video-player-type="h5-page"
          x5-video-player-fullscreen="false"
          // T7/U4（vivo 自带浏览器、UC 系）不认 x5-*，独立走 t7-video-player-type。
          // X5/MIUI 会忽略这个属性，加上不冲突。
          t7-video-player-type="inline"
          preload={isLazy ? 'none' : 'auto'}
          tabIndex={enableSpaceShortcut ? 0 : undefined}
          onCanPlay={() => onReadyRef.current?.()}
          onClick={handleClickVideo ? (event) => event.preventDefault() : undefined}
          onEnded={handleEnded}
          onError={handleError}
          onContextMenu={(event) => event.preventDefault()}
        />
        {showStartPlugin && !isPlaying && (
          <button
            type="button"
            className="native-video-start-button"
            aria-label="Play"
            data-native-video-chrome="true"
            onClick={handleStartClick}
          >
            <span className="native-video-start-icon" />
          </button>
        )}
      </div>
    );
  },
);

BaseVideoPlayer.displayName = 'BaseVideoPlayer';
